import { useCallback } from 'react'
import { useApolloClient } from '@apollo/client/react'
import { renovarAccessToken } from '@/lib/auth/renovacion'
import { ME } from '../api/queries'

/**
 * Hook para forzar un refresh manual del access token + refetch del perfil.
 *
 * Cuándo usarlo: cuando una operación de negocio cambia el rol del usuario en
 * backend (ej. `portalCreateBusinessWithOrg` convierte un Lead en ORG_OWNER).
 * El JWT actual sigue siendo válido pero con el rol viejo — hace falta pedir
 * uno nuevo para que `RoleGuard`, `Can` y el sidebar reflejen los permisos
 * actualizados.
 *
 * Reusa `renovarAccessToken` del módulo canónico de sesión: comparte el
 * single-flight con el refresh automático del errorLink y guarda el token nuevo
 * en el store. `refetchQueries({ include: [ME] })` invalida el cache.
 *
 * Es un refresh de CORTESÍA (el token actual sigue siendo válido, solo trae el
 * rol viejo): por eso NO aplica la política de la web privada — un desenlace
 * `sesion-invalida` o `red` devuelve `null` y el llamador sigue con el token que
 * ya tenía, sin sacar a nadie al login.
 */
export function useRefreshAuth() {
  const client = useApolloClient()

  const refresh = useCallback(async (): Promise<string | null> => {
    const desenlace = await renovarAccessToken()
    if (desenlace.tipo !== 'renovado') return null
    await client.refetchQueries({ include: [ME] })
    return desenlace.token
  }, [client])

  return { refresh }
}
